import React, { useState, useRef } from 'react';
import { useNavigate, Link } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faFolderOpen, faBrain, faUsers, faFilePdf, faVolumeUp, faDownload, faSitemap, faCog } from '@fortawesome/free-solid-svg-icons';
import { library } from '@fortawesome/fontawesome-svg-core';

library.add(faPlus, faFolderOpen, faBrain, faUsers, faFilePdf, faVolumeUp, faDownload, faSitemap, faCog);

const PahijeORS = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [parsedData, setParsedData] = useState(null);
  const [fitScore, setFitScore] = useState(0);
  const [breakdown, setBreakdown] = useState({});
  const [elevatorPitch, setElevatorPitch] = useState('');
  const [resumeText, setResumeText] = useState(`Jane Rao
    Senior Product Manager
    Experience:
    - Product Manager, Gamma Inc (2020-2024): led payments feature; increased GMV by 15%.
    - Associate PM, Delta (2018-2020): metrics and experiments.
    Skills: Product Strategy, Roadmapping, SQL, A/B Testing
    Education: MBA, IIM Ahmedabad`);
  const fileInputRef = useRef(null);

  const navItems = [
    { id: 'overview', label: 'Overview & Architecture' },
    { id: 'resume', label: 'ResumeVault (Entry)' },
    { id: 'parsed', label: 'Parsed View' },
    { id: 'scoring', label: 'Scoring & Insights' },
    { id: 'exports', label: 'Formats & Exports' },
    { id: 'agents', label: 'Agentic Orchestrator' },
    { id: 'apis', label: 'APIs & OpenAPI Mock' },
    { id: 'ors', label: 'ORS Schema' },
    { id: 'pipeline', label: 'AI Pipeline' },
  ];

  const ORSExample = {
    id: "r_001",
    name: "Jane Rao",
    contacts: { email: "jane@example.com" },
    summary: "Product leader with experience in payments",
    experience: [
        {
        company: "Gamma",
        from: "2020-01",
        to: "2024-06",
        role: "PM",
        highlights: ["Increased GMV 15%"]
        }
    ],
    skills: [
        { id: "skill_nodejs", name: "Node.js", level: 0.8 }
    ],
    education: [],
    versions: [],
    createdAt: "2025-12-12"
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/plain') {
      const reader = new FileReader();
      reader.onload = (e) => {
        setResumeText(e.target.result);
        setActiveSection('resume');
      };
      reader.readAsText(file);
    } else {
      alert('Please upload a .txt file.');
    }
  };

  const demoParse = (text) => {
    const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
    const name = lines[0] || '—';
    const skillsLine = lines.find(l => /^Skills:/i.test(l)) || '';
    const skills = skillsLine.replace(/^Skills:/i, '').split(',').map(s => s.trim()).filter(Boolean);
    const expLines = lines.filter(l => / at |\(|\d{4}/i.test(l));
    const parsed = {
      id: 'r_demo',
      name,
      contacts: {},
      summary: lines.slice(1, 2).join(' '),
      skills,
      experience: expLines,
    };
    setParsedData(parsed);
    const fit = Math.round(60 + parsed.skills.length * 7 + (parsed.experience.length * 5));
    setFitScore(Math.min(fit, 99));
    setBreakdown({
      skills: Math.min(45, parsed.skills.length * 9),
      experience: Math.min(30, parsed.experience.length * 6),
      projects: 10,
      communication: 5,
    });
    setElevatorPitch(`${parsed.name} — ${parsed.summary || 'Candidate'}. Key skills: ${parsed.skills.join(', ')}`);
    return parsed;
  };

  const handleParse = () => {
    const parsed = demoParse(resumeText);
    setActiveSection('parsed');
    // Show other sections as needed
  };

  const handleDownloadJson = () => {
    if (parsedData) {
      const json = JSON.stringify(parsedData, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'pahije_resume.json';
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const handleAudio = () => {
    const text = elevatorPitch || 'Candidate elevator pitch.';
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      window.speechSynthesis.speak(utterance);
    } else {
      alert('TTS not supported in this browser preview');
    }
  };

  const handlePdf = () => {
    alert('Server-side PDF rendering demo (not available in preview)');
  };

  const handleLinkedIn = () => {
    alert('LinkedIn rewrite demo — in production would call LLM');
  };

  const handleAtsExport = () => {
    if (parsedData) {
      alert('Dummy ATS Integration: Resume exported to Greenhouse. In production, this would POST to ATS API with ORS JSON.');
    } else {
      alert('No parsed data available. Please parse a resume first.');
    }
  };

  const triggerFileUpload = () => {
    fileInputRef.current?.click();
  };

  const sections = {
    overview: (
      <div className="space-y-4">
        <div>
          <h2 className="text-lg font-bold text-blue-100">Full System Architecture (high-level)</h2>
          <p className="text-xs text-gray-100">Single platform unifying sourcing, parsing, matching, scoring, generation & orchestration.</p>
        </div>
        <pre className="bg-gray-800 border border-gray-700 p-3 rounded-md text-xs whitespace-pre-wrap break-words">
          Clients (React Dashboard / Candidate Portal) {'->'} API Gateway {'->'} Agent Orchestrator {'->'} {'{Resume Parser, Role-Ontology, Match Service, Generator}'} {'->'} Datastores & Index
        </pre>
        <div>
          <h3 className="text-sm font-bold text-blue-400">Key Services</h3>
          <ul className="text-sm space-y-1 mt-2 [&_li]:text-white">
            <li>ResumeVault DB (MongoDB) + S3 file store</li>
            <li>Resume Parser (NER, OCR fallback)</li>
            <li>Skill Extractor & Ontology</li>
            <li>Match & Scoring Service (interpretable)</li>
            <li>Generator: PDF, TTS, LinkedIn, Audio</li>
            <li>Agentic Orchestrator: workflows, scheduling, human-in-loop</li>
            <li>Search & Vector Index (OpenSearch / Milvus)</li>
          </ul>
        </div>
        <div>
          <h3 className="text-sm font-bold text-blue-400">Security & Governance</h3>
          <ul className="text-sm space-y-1 mt-2  [&_li]:text-white">
            <li>SSO, RBAC, PII redaction, consented data sharing</li>
            <li>Audit logs for ML decisions (for bias checks)</li>
          </ul>
        </div>
      </div>
    ),
    resume: (
      <div className="space-y-4">
        <div>
          <h2 className="text-lg font-bold text-blue-400">ResumeVault — Plain Text Entry</h2>
          <p className="text-sm text-gray-400">Paste or upload a plain text resume. The system creates a versioned canonical record (ORS).</p>
        </div>
        <textarea
          value={resumeText}
          onChange={(e) => setResumeText(e.target.value)}
          className="w-full min-h-[220px] p-3 border border-gray-700 rounded-md font-mono resize-vertical bg-gray-800 text-white text-sm"
          placeholder="Paste your resume here..."
        />
        <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
          <button onClick={handleParse} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium">
            Parse & Send to Vault
          </button>
          <p className="text-sm text-gray-100">Autosave to ResumeVault (versioned)</p>
        </div>
      </div>
    ),
    parsed: (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-2">
          <p className="text-sm text-gray-100">Parsed Fields</p>
          <pre className="bg-gray-800 border border-gray-700 p-3 rounded-md overflow-auto text-xs">
            {parsedData ? JSON.stringify(parsedData, null, 2) : '—'}
          </pre>
        </div>
        <div className="space-y-2">
          <p className="text-sm text-gray-100">Quick Edits / Approve</p>
          <textarea
            value={parsedData ? JSON.stringify(parsedData, null, 2) : ''}
            className="w-full min-h-[160px] p-3 border border-gray-700 rounded-md font-mono resize-vertical bg-gray-800 text-white text-sm"
            placeholder="Approve parsed JSON here for export."
          />
        </div>
      </div>
    ),
    scoring: (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-2">
          <p className="text-sm text-gray-100">RoleFit Score</p>
          <div className="text-4xl font-extrabold text-blue-400">{fitScore}/100</div>
          <p className="text-sm text-gray-400">Feature Breakdown</p>
          <pre className="bg-gray-800 border border-gray-700 p-3 rounded-md overflow-auto text-xs">
            {JSON.stringify(breakdown, null, 2)}
          </pre>
        </div>
        <div className="space-y-2">
          <p className="text-sm text-gray-100">Suggested Actions</p>
          <ul className="text-sm space-y-1  [&_li]:text-white">
            <li>Phone screen (15m)</li>
            <li>Technical deep dive (45m)</li>
            <li>Manager sync (30m)</li>
          </ul>
        </div>
      </div>
    ),
    exports: (
      <div className="space-y-4">
        <div>
          <h2 className="text-lg font-bold text-blue-400">Formats & Exports</h2>
          <div className="flex flex-wrap gap-2 mt-4">
            <button onClick={handlePdf} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium">
              <FontAwesomeIcon icon={faFilePdf} /> Generate Graphical PDF
            </button>
            <button onClick={handleAudio} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium">
              <FontAwesomeIcon icon={faVolumeUp} /> Generate Audio Résumé
            </button>
            <button onClick={handleLinkedIn} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium">
              <FontAwesomeIcon icon={faSitemap} /> Rewrite for LinkedIn
            </button>
            <button onClick={handleDownloadJson} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium" disabled={!parsedData}>
              <FontAwesomeIcon icon={faDownload} /> Download ORS JSON
            </button>
            <button onClick={handleAtsExport} className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-medium" disabled={!parsedData}>
              Export to ATS (Greenhouse)
            </button>
          </div>
        </div>
        <div>
          <p className="text-sm text-gray-400">Preview — Elevator Pitch</p>
          <pre className="bg-gray-800 border border-gray-700 p-3 rounded-md overflow-auto text-xs">
            {elevatorPitch || '—'}
          </pre>
        </div>
      </div>
    ),
    agents: (
      <div className="space-y-4">
        <div>
          <h2 className="text-lg font-bold text-blue-400">Agentic Orchestrator</h2>
          <p className="text-sm text-gray-100">Orchestrates workflows: Sourcing → Parsing → Screening → Interviewing → Offer. Agents include:</p>
          <ul className="text-sm space-y-2 mt-2  [&_li]:text-white">
            <li><strong>Hiring Strategist:</strong> forecasts hiring needs</li>
            <li><strong>Functional Fit Agent:</strong> maps candidates to team needs</li>
            <li><strong>HR Intelligence Agent:</strong> builds interview kits and scores</li>
            <li><strong>Screening Agent:</strong> performs initial resume screening and shortlisting</li>
            <li><strong>Interview Orchestrator:</strong> schedules interviews, coordinates panels, and tracks feedback</li>
            <li><strong>Offer Generator:</strong> creates personalized offer letters based on role and candidate profile</li>
            <li><strong>Onboarding Assistant:</strong> handles post-hire documentation, training schedules, and team introductions</li>
          </ul>
          <p className="text-sm text-gray-400 mt-2">Workflow editor (visual) and rules engine allow human overrides at any step. Audit logs are recorded for each agent decision.</p>
        </div>
        <div>
          <h3 className="text-sm font-bold text-blue-400">Flow Diagram</h3>
          <svg viewBox="0 0 900 220" className="w-full h-auto" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <style dangerouslySetInnerHTML={{__html: `
                .box { fill: #1e3a4a; stroke: #0b6fb0; stroke-width: 1.2; }
                .text { font: 13px sans-serif; fill: #e2e8f0; }
                .arrow { stroke: #60a5fa; stroke-width: 2; fill: none; marker-end: url(#arr); }
              `}} />
              <marker id="arr" markerWidth="10" markerHeight="10" refX="6" refY="5" orient="auto">
                <path d="M0 0 L10 5 L0 10 z" fill="#60a5fa" />
              </marker>
            </defs>
            <rect x="20" y="20" rx="8" ry="8" width="170" height="50" className="box" />
            <text x="40" y="50" className="text">Sourcing & Ingest</text>
            <rect x="220" y="20" rx="8" ry="8" width="170" height="50" className="box" />
            <text x="250" y="50" className="text">Resume Parser</text>
            <rect x="420" y="20" rx="8" ry="8" width="170" height="50" className="box" />
            <text x="455" y="50" className="text">Candidate Store & Index</text>
            <rect x="620" y="20" rx="8" ry="8" width="230" height="50" className="box" />
            <text x="670" y="50" className="text">Agentic Orchestrator (Workflows)</text>
            <path d="M190 45 L220 45" className="arrow" />
            <path d="M390 45 L420 45" className="arrow" />
            <path d="M590 45 L620 45" className="arrow" />
            <rect x="320" y="120" rx="8" ry="8" width="240" height="50" className="box" />
            <text x="350" y="150" className="text">Match & Scoring Service</text>
            <path d="M560 45 L440 120" className="arrow" />
            <path d="M440 120 L560 140" className="arrow" />
          </svg>
        </div>
      </div>
    ),
    apis: (
      <div className="space-y-4">
        <h2 className="text-lg font-bold text-blue-400">Mock API (OpenAPI-style)</h2>
        <pre className="bg-gray-800 border border-gray-700 p-3 rounded-md overflow-auto text-xs">
            {`POST /api/v1/resume/upload (file or text)
            Response: { resumeId }
            POST /api/v1/resume/parse
            Body: { resumeId }
            Response: { parsedJSON }
            POST /api/v1/resume/score
            Body: { resumeId, roleId }
            Response: { roleFitScore, breakdown, explanation }
            GET /api/v1/resume/{id}/export?format=pdf|audio|json|txt
            POST /api/v1/ats/export
            Body: { resumeId, atsProvider: "greenhouse" }
            Response: { integrationStatus: "success" }`}
        </pre>
        <p className="text-sm text-gray-400">Also supports webhooks for downstream ATS and integrations (LinkedIn, Greenhouse).</p>
      </div>
    ),
    ors: (
      <div className="space-y-4">
        <h2 className="text-lg font-bold text-blue-400">Open Resume Schema (ORS) — Example</h2>
        <pre className="bg-gray-800 border border-gray-700 p-3 rounded-md overflow-auto text-xs">
            {`{
            "id":"r_001",
            "name":"Jane Rao",
            "contacts":{"email":"jane@example.com"},
            "summary":"Product leader with experience in payments",
            "experience":[{"company":"Gamma","from":"2020-01","to":"2024-06","role":"PM","highlights":["Increased GMV 15%"]}],
            "skills":[{"id":"skill_nodejs","name":"Node.js","level":0.8}],
            "education":[],
            "versions":[],
            "createdAt":"2025-12-12"
            }`}
        </pre>
      </div>
    ),
    pipeline: (
      <div className="space-y-4">
        <h2 className="text-lg font-bold text-blue-400">AI Pipeline (detailed)</h2>
        <ol className="text-sm space-y-2 list-decimal pl-4 [&_li]:text-gray-200">
          <li><strong>Preprocessor:</strong> normalize dates, split sections, language detection.</li>
          <li><strong>Parser:</strong> NER + heuristics for roles, dates, projects.</li>
          <li><strong>Skill Mapper:</strong> embeddings + ontology mapping to canonical skill IDs.</li>
          <li><strong>Scoring Engine:</strong> compute S,E,P,G,R,C features; apply weights per role.</li>
          <li><strong>Generator:</strong> HTML/PDF renderer, TTS, LinkedIn rewrite, audio export.</li>
          <li><strong>Human-in-loop:</strong> approval flows, audit, re-training triggers.</li>
        </ol>
        <p className="text-sm text-gray-400">Start with interpretable models (XGBoost / Logistic) and add LLM-based explanation layer (ChatGPT) for human-readable rationales.</p>
      </div>
    ),
  };

  return (
    <div className="min-h-screen bg-black text-white font-sans p-4 md:p-6">
      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".txt"
        onChange={handleFileUpload}
        className="hidden"
      />

      {/* Header */}
      <header className="flex flex-col lg:flex-row justify-between mb-2 gap-2">
        <div className="flex-1">
          <h1 className="text-lg lg:text-xl font-bold text-yellow-200 mb-2 text-left">Think5Whys Pahije — Agentic HR & ResumeVault</h1>
          <p className="text-xs text-gray-400 leading-relaxed">
            Unified platform combining Agentic AI recruitment architecture and the Think5Whys ResumeVault. Paste plain-text resumes, run AI agents, export formats, and orchestrate hiring workflows.
          </p>
        </div>
        <div className="flex items-right gap-3">
          {/* <div className="text-gray-400 text-sm">Canvas Preview</div>
          <div className="w-3" /> */}
          <div className="text-gray-500 text-xs">Build: v1.0</div>
        </div>
      </header>
      <section className="mt-8 pt-6 border-t border-gray-800 text-center">
        <div className="space-y-4">
            <p className="text-sm text-gray-100">
            Pahije is a cloud-based Applicant Tracking System (ATS) by Think5Whys Global DeepTech Solutions, designed to streamline recruitment processes for companies. It helps manage the entire hiring workflow by organizing and tracking job applications, resumes, candidate information, sourcing, interviews, and talent management. Unlike basic ATS tools, it incorporates built-in AI features for more efficient hiring, such as candidate matching and automation. Launched in 2025, it's popular for its customizable, scalable interface that supports teams of all sizes.
            </p>
            <p className="text-sm text-gray-100">
            Pahije is an Intelligent Agentic AI Orchestrator for inefficient manual HR jobs that are prone to time-taking tasks and full of human bias.
            </p>
        </div>
       </section>
      {/* Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,300px)_1fr] gap-1">
        {/* Sidebar */}
        <aside className="bg-gray-900 border border-gray-800 rounded-lg p-4 w-full flex flex-col gap-6 lg:order-1">
            {/* Navigation */}
            <nav className="mb-6">
            <ul className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                {navItems.map((item) => (
                <li
                    key={item.id}
                    className={`p-3 rounded-md text-white cursor-pointer text-sm transition-colors text-center ${
                    activeSection === item.id
                        ? 'bg-blue-900/20 border border-blue-500/30'
                        : 'hover:bg-gray-800'
                    }`}
                    onClick={() => setActiveSection(item.id)}
                >
                    {item.label}
                </li>
                ))}
            </ul>
            </nav>

            {/* Quick Actions */}
            <section className="bg-gray-800 p-4 rounded-lg">
            <h3 className="text-sm font-bold text-blue-400 mb-3 flex items-center gap-2">
                <FontAwesomeIcon icon={faCog} /> Quick Actions
            </h3>
            <div className="flex flex-wrap gap-2">
                <button className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-full text-xs font-medium">
                <FontAwesomeIcon icon={faPlus} /> New Resume
                </button>
                <button className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-full text-xs font-medium">
                <FontAwesomeIcon icon={faFolderOpen} /> Import .txt
                </button>
                <button className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-full text-xs font-medium">
                <FontAwesomeIcon icon={faBrain} /> Run Parser
                </button>
                <button className="flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-full text-xs font-medium" disabled>
                Sync with ATS
                </button>
            </div>
            </section>

            {/* Roles */}
            <section className="bg-blue-900/20 p-4 rounded-lg">
            <h3 className="text-sm font-bold text-blue-400 mb-3 flex items-center gap-2">
                <FontAwesomeIcon icon={faUsers} /> Roles
            </h3>
            <div className="flex flex-wrap gap-2">
                {['Director', 'Hiring Manager', 'HR', 'Candidate', 'Agent'].map((role) => (
                <span key={role} className="px-3 py-1 bg-blue-800/30 border border-blue-500/30 rounded-full text-xs">
                    {role}
                </span>
                ))}
            </div>
            </section>
        </aside>

        {/* Main Content */}
        <main className="space-y-6 order-1 lg:order-2">
            <section
            className={`bg-gray-900 border border-gray-800 rounded-lg p-4 ${
                activeSection === 'overview' ? 'block' : 'hidden'
            }`}
            >
            {sections[activeSection] || sections.overview}
            </section>
            {navItems.slice(1).map((item) => (
            <section
                key={item.id}
                className={`bg-gray-900 border border-gray-800 rounded-lg p-4 ${
                activeSection === item.id ? 'block' : 'hidden'
                }`}
            >
                {sections[item.id]}
            </section>
            ))}
        </main>
        </div>
        <section className="mt-8 pt-6 border-t border-gray-800 text-center">
          <div className="space-y-4">
              <div>
                <h4 className="text-xs font-semibold text-blue-400 mb-2 uppercase tracking-wide">What is ORS?</h4>
                <p className="text-xs text-gray-100 mb-3">
                    ORS stands for Open Resume Schema, a structured JSON-based format for representing resumes and CVs in a standardized, machine-readable way. It's closely associated with OpenResume, an open-source project that includes a resume builder, parser, and schema for extracting and validating professional resume data (e.g., name, experience, skills, education). This schema enables easy parsing from PDFs or text, validation, and integration into tools like ATS systems or AI parsers.
                </p>
                <div className="text-left">
                    <pre className="bg-gray-800 border border-gray-700 rounded-md p-3 overflow-auto text-xs font-mono">
                    <code>{JSON.stringify(ORSExample, null, 2)}</code>
                    </pre>
                </div>
              </div>
          </div>
       </section>        
        <footer className="bg-black border-t border-blue-400 text-white py-4">
            <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
            <div className="text-sm text-yellow-200">© 2026 Think5Whys Global DeepTech Solutions.</div>
            <div className="flex flex-wrap gap-4 text-sm">
                <Link to="/about-us" className="hover:text-teal-300">About</Link>
                <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
                <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
                <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
            </div>
            </div>
        </footer>       
    </div>
  );
};

export default PahijeORS;